'use client';

import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["className", "classNames", "showOutsideDays", "yearDropdown", "footer"];
var _ArrowIosLeft, _SelectTrigger, _ArrowIosRight;
import * as React from 'react';
import { DayPicker, useNavigation } from 'react-day-picker';
import { format } from 'date-fns';
import FillArrowDown from '@crehana/compass.icons.fill/FillArrowDown';
import ArrowIosLeft from '@crehana/compass.icons.outline/ArrowIosLeft';
import ArrowIosRight from '@crehana/compass.icons.outline/ArrowIosRight';
import { cn } from "../utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../Select";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function createArrayFromRange() {
  var from = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : new Date().getFullYear() - 80;
  var to = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : new Date().getFullYear() + 10;
  var fixedTo = to < from ? from : to;
  return Array.from({
    length: fixedTo - from + 1
  }, function (_, i) {
    return from + i;
  });
}
function CustomCaption(props) {
  var _useNavigation = useNavigation(),
    goToMonth = _useNavigation.goToMonth,
    nextMonth = _useNavigation.nextMonth,
    previousMonth = _useNavigation.previousMonth;
  var btnClassName = function btnClassName(enabled) {
    return cn('tw-grow-2', {
      'tw-opacity-50 tw-cursor-not-allowed': !enabled
    });
  };
  var yearOptions = createArrayFromRange(props.fromYear, props.toYear);
  return /*#__PURE__*/_jsxs("div", {
    className: "tw-w-full tw-flex tw-gap-0.4 tw-items-center tw-justify-between tw-p-0.4",
    children: [/*#__PURE__*/_jsx("button", {
      type: "button",
      disabled: !previousMonth,
      onClick: function onClick() {
        return previousMonth && goToMonth(previousMonth);
      },
      className: btnClassName(Boolean(previousMonth)),
      children: _ArrowIosLeft || (_ArrowIosLeft = /*#__PURE__*/_jsx(ArrowIosLeft, {
        size: 24,
        className: "tw-w-2.4 tw-h-2.4 tw-bg-transparent tw-p-0 tw-text-primary-light-500"
      }))
    }), /*#__PURE__*/_jsx("div", {
      className: "tw-w-[22.4rem] tw-text-center tw-font-body4 tw-capitalize",
      children: props.yearDropdown ? /*#__PURE__*/_jsxs("span", {
        className: "tw-flex tw-items-center tw-justify-center tw-gap-0.4",
        children: [format(props.displayMonth, 'MMMM', {
          locale: props.locale
        }), /*#__PURE__*/_jsxs(Select, {
          defaultValue: props.displayMonth.getFullYear().toString(),
          value: props.displayMonth.getFullYear().toString(),
          onValueChange: function onValueChange(value) {
            var newDate = new Date(props.displayMonth);
            newDate.setFullYear(parseInt(value));
            goToMonth(newDate);
          },
          children: [_SelectTrigger || (_SelectTrigger = /*#__PURE__*/_jsx(SelectTrigger, {
            className: "tw-text-primary-light-500 tw-font-subtitle5 dark:tw-text-primary-dark-400",
            unstyled: true,
            children: /*#__PURE__*/_jsxs("span", {
              className: "tw-flex tw-items-center tw-gap-0.4",
              children: [/*#__PURE__*/_jsx(SelectValue, {}), /*#__PURE__*/_jsx(FillArrowDown, {
                className: "tw-text-inherit",
                size: 16
              })]
            })
          })), /*#__PURE__*/_jsx(SelectContent, {
            children: yearOptions.map(function (year) {
              return /*#__PURE__*/_jsx(SelectItem, {
                value: year.toString(),
                children: year
              }, year);
            })
          })]
        })]
      }) : format(props.displayMonth, 'MMMM yyy', {
        locale: props.locale
      })
    }), /*#__PURE__*/_jsx("button", {
      type: "button",
      disabled: !nextMonth,
      onClick: function onClick() {
        return nextMonth && goToMonth(nextMonth);
      },
      className: btnClassName(Boolean(nextMonth)),
      children: _ArrowIosRight || (_ArrowIosRight = /*#__PURE__*/_jsx(ArrowIosRight, {
        size: 24,
        className: "tw-w-2.4 tw-h-2.4 tw-bg-transparent tw-p-0 tw-text-primary-light-500"
      }))
    })]
  });
}
function Calendar(_ref) {
  var className = _ref.className,
    classNames = _ref.classNames,
    _ref$showOutsideDays = _ref.showOutsideDays,
    showOutsideDays = _ref$showOutsideDays === void 0 ? true : _ref$showOutsideDays,
    yearDropdown = _ref.yearDropdown,
    footer = _ref.footer,
    props = _objectWithoutProperties(_ref, _excluded);
  var isSingleMode = props.mode === 'single';
  var isRangeMode = props.mode === 'range';
  return /*#__PURE__*/_jsx(DayPicker, _objectSpread({
    locale: props.locale,
    showOutsideDays: showOutsideDays,
    className: cn('tw-px-1.6 tw-pt-2 tw-pb-3.2 tw-shadow-dropdown tw-rounded dark:tw-text-neutral-light-100', className),
    classNames: _objectSpread({
      // root
      months: 'tw-flex tw-flex-col',
      month: '',
      caption: 'tw-flex tw-justify-center tw-pt-0.4 tw-relative tw-items-center',
      caption_label: 'tw-font-body4',
      nav: 'tw-space-x-0.4 flex items-center',
      nav_button: 'tw-w-2.4 tw-h-2.4 tw-bg-transparent tw-p-0 tw-text-primary-light-500',
      // nav_button_previous: 'absolute left-1',
      // nav_button_next: 'absolute right-1',
      table: 'tw-mt-1.2 tw-block tw-w-full',
      head_row: 'tw-flex tw-justify-between tw-px-0.4 tw-pb-2',
      head_cell: 'tw-font-label1 tw-uppercase tw-block tw-w-2.4 tw-h-[1.8rem] tw-text-center tw-text-neutral-light-600',
      tbody: 'tw-space-y-1.2',
      row: 'tw-flex tw-flex-row',
      cell: 'tw-p-0 tw-cursor-pointer focus-within:tw-relative',
      day: cn('tw-w-[4.2rem] tw-h-2.4 tw-text-center tw-font-body4', isSingleMode && 'hover:tw-bg-primary-light-300 hover:tw-text-neutral-light-100 tw-rounded-[40px] dark:hover:tw-bg-primary-dark-400'),
      day_outside: 'tw-text-neutral-light-700 dark:tw-text-neutral-dark-600 aria-selected:tw-text-neutral-light-100 dark:aria-selected:tw-text-neutral-light-100',
      day_selected: cn(isSingleMode && 'tw-bg-primary-light-500 hover:tw-bg-primary-light-500 dark:hover:tw-bg-primary-light-500 tw-text-neutral-light-100 tw-rounded-[40px]', isRangeMode && 'tw-bg-primary-light-500 hover:tw-bg-primary-light-500 dark:hover:tw-bg-primary-light-500 tw-text-neutral-light-100'),
      day_range_start: 'tw-rounded-l-full tw-bg-primary-light-500 tw-text-neutral-light-100',
      day_range_end: 'tw-rounded-r-full tw-bg-primary-light-500 tw-text-neutral-light-100',
      day_today: 'tw-font-semibold',
      day_disabled: 'tw-opacity-20',
      day_range_middle: 'aria-selected:bg-slate-100 aria-selected:text-slate-900 dark:aria-selected:bg-slate-800 dark:aria-selected:text-slate-50',
      day_hidden: 'tw-invisible'
    }, classNames),
    components: {
      Caption: function Caption(captionProps) {
        var _props$fromYear, _props$fromDate, _props$toYear, _props$toDate;
        return /*#__PURE__*/_jsx(CustomCaption, _objectSpread({
          locale: props.locale,
          yearDropdown: yearDropdown,
          fromYear: (_props$fromYear = props.fromYear) !== null && _props$fromYear !== void 0 ? _props$fromYear : (_props$fromDate = props.fromDate) === null || _props$fromDate === void 0 ? void 0 : _props$fromDate.getFullYear(),
          toYear: (_props$toYear = props.toYear) !== null && _props$toYear !== void 0 ? _props$toYear : (_props$toDate = props.toDate) === null || _props$toDate === void 0 ? void 0 : _props$toDate.getFullYear()
        }, captionProps));
      }
    },
    footer: footer
  }, props));
}
Calendar.displayName = 'Calendar';
export { Calendar };